/**
 * Translation, plus the current language.
 *
 * The language is held at module scope, not only in React state, so the two
 * places that render outside every provider can still translate: the
 * ErrorBoundary (which wraps the providers) and the 401 message thrown by
 * api/client.ts (which is not in React at all).
 *
 * Components must take `t` from useI18n() rather than importing it here - an
 * import does not subscribe to the context, so the component would keep its
 * old text until something else re-rendered it.
 */

import { de, en, type MsgKey } from "@/i18n/messages";

export const LANGS = ["de", "en"] as const;
export type Lang = (typeof LANGS)[number];

const STORAGE_KEY = "ai2sales.lang";

/** German unless the user has chosen otherwise; a stale or junk value falls back. */
function stored(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  return LANGS.includes(saved as Lang) ? (saved as Lang) : "de";
}

let current: Lang = stored();

export function getLang(): Lang {
  return current;
}

export function applyLang(lang: Lang): void {
  current = lang;
  localStorage.setItem(STORAGE_KEY, lang);
  // index.html hard-codes lang="de" as the pre-render default.
  document.documentElement.lang = lang;
}

export function t(key: MsgKey, vars?: Record<string, string | number>): string {
  const message = (current === "en" ? en : de)[key];
  if (!vars) return message;
  return message.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Intl locale for the current language, for dates and currency. */
export function intlLocale(lang: Lang): string {
  return lang === "en" ? "en-GB" : "de-DE";
}
